import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  debounceTime,
  finalize,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { LookupItem } from '../../../../core/models/lookup.models';
import { CommonService } from '../../../../shared/services/common.service';
import {
  ApiResponses,
  BIP,
  BipTemplateListModel,
  CaseDetailModel,
  EditCaseDrawerParams,
} from '../../insights.interface';
import { InsightsService } from '../../insights.service';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { CommonModule } from '@angular/common';
import { StudentSearchModel } from '../../../manage-organization/insights/insights.interface';
import { DrawerService } from '../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { differenceInCalendarDays, format } from 'date-fns';
import { OrganizationService } from '../../../organizations/organization.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { InsightMode } from '../../insights.enums';
import { CreateBipModel } from '../../insights.interface';
import { ScheduleTemplate } from '../../../manage-settings/manage-settings.interface';
import { CustomTimeService } from '../../../manage-settings/custom-time/custom-time.service';
import { AuthService } from '../../../../core/services/auth.service';
import {
  OrganizationDetailModel,
  userDetailModel,
} from '../../../organizations/organization.interface';
import { ManageOrganizationService } from '../../../manage-organization/manage-organization.service';
import { LabelKey } from '../../../../core/models/report-labels.model';
import { ReportLabelsService } from '../../../manage-settings/report-labels/report-labels.service';
import { ReportLabelPipe } from '../../../../core/pipes/report-label.pipe';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { ViewBipCaseComponent } from '../view-bip-case/view-bip-case.component';
import { ApiResponse } from '../../../../core/models/api-response.models';
import _ from 'lodash';

@Component({
  selector: 'app-create-bip-case',
  standalone: true,
  imports: [
    CommonModule,
    NzFormModule,
    NzFormItemComponent,
    NzFormItemComponent,
    NzFormControlComponent,
    NzInputModule,
    NzIconModule,
    NzSelectModule,
    NzDatePickerModule,
    NzButtonModule,
    SvgIconComponent,
    NzRadioModule,
    ReactiveFormsModule,
    FormsModule,
    NzAutocompleteModule,
    NzIconModule,
    NzSpinModule,
    ReportLabelPipe,
    NzModalModule,
  ],
  templateUrl: './create-bip-case.component.html',
  styleUrl: './create-bip-case.component.scss',
})
export class CreateBipCaseComponent implements OnInit, OnDestroy {
  bipForm: FormGroup = {} as FormGroup;
  genderList: LookupItem[] = [];
  raceList: LookupItem[] = [];
  specialEducationList: LookupItem[] = [];
  languageLevelList: LookupItem[] = [];
  organizationList: LookupItem[] = [];
  generalEducationList: LookupItem[] = [];
  studentList: StudentSearchModel[] = [];
  bipTemplateList: BipTemplateListModel[] = [];
  customScheduleList: ScheduleTemplate[] = [];
  selectedStudent: StudentSearchModel | null = null;
  selectedOrg: number | null = null;
  caseParams: EditCaseDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.CREATE,
  };
  searchInput = new Subject<string>();
  loading$: Observable<boolean>;
  loading = false;
  today = new Date();
  disabledDate = (current: Date): boolean =>
    differenceInCalendarDays(current, this.today) > 0;
  caseData: CreateBipModel | null = null;
  isInitialOrgLoad = true;
  orgData: OrganizationDetailModel | null = null;
  orgId: number | null = null;
  userData: userDetailModel | null = null;
  private readonly destroy$ = new Subject<void>();
  isStudentSearchLoading = false;
  isOrganizationSearchLoading = false;
  isBipTemplateLoading = false;
  isScheduleLoading = false;
  needSafetyPlanLabel: string = LabelKey.NEEDS_SAFETY_PLAN;
  isTrainingWarningVisible = false;
  previousTrainingValue = false;
  IsIncludeDefaultSchedule = true;

  constructor(
    private insightsService: InsightsService,
    private commonService: CommonService,
    private cd: ChangeDetectorRef,
    private drawerService: DrawerService,
    private orgService: OrganizationService,
    private loaderService: LoaderService,
    private scheduleService: CustomTimeService,
    private authService: AuthService,
    private manageOrgService: ManageOrganizationService,
    private reportLabelsService: ReportLabelsService
  ) {
    this.reportLabelsService
      .getLabelValue(LabelKey.NEEDS_SAFETY_PLAN)
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.needSafetyPlanLabel = value;
      });
    this.authService.orgData$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.orgData = res;
      this.orgId = this.orgData?.organizationId || 0;
    });
    this.authService.userData$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.userData = res;
    });
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  async ngOnInit() {
    this.bipForm = this.insightsService.bipForm(
      this.caseData ? this.caseData : null,
      ['caseBehaviorId', 'caseBehaviorsType', 'caseOwnerId']
    );
    await this.getMasterData();

    this.bipForm
      .get('studentId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(newStudentId => {
        if (newStudentId) {
          const currentFormValues = this.bipForm.getRawValue();
          const preservedValues = {
            specialEducations: currentFormValues.specialEducations,
            generalEducations: currentFormValues.generalEducations,
            languageLevelId: currentFormValues.languageLevelId,
            note: currentFormValues.note,
            isTrainingStudent: currentFormValues.isTrainingStudent,
            isEnabledSafetyPlan: currentFormValues.isEnabledSafetyPlan,
            bipTemplateId: currentFormValues.bipTemplateId,
            customScheduleId: currentFormValues.customScheduleId,
          };

          setTimeout(() => {
            if (this.bipForm) {
              Object.keys(preservedValues).forEach(key => {
                if (
                  preservedValues[key as keyof typeof preservedValues] !==
                    undefined &&
                  preservedValues[key as keyof typeof preservedValues] !== null
                ) {
                  this.bipForm
                    .get(key)
                    ?.setValue(
                      preservedValues[key as keyof typeof preservedValues]
                    );
                }
              });
            }
          });
        }
      });

    this.bipForm.controls['studentSearch'].valueChanges
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe((val: string) => {
        if (val === '') {
          this.onBlurStudent('');
          this.studentList = [];
          this.selectedStudent = null;
        }
      });

    this.bipForm.controls['organizationId'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: string) => {
        if (!val) {
          this.bipForm.controls['bipTemplateId'].setValue(null);
          this.bipForm.controls['bipTemplateId'].disable();
          this.bipForm.controls['customScheduleId'].setValue(null);
          this.bipForm.controls['customScheduleId'].disable();
        } else {
          this.bipForm.controls['bipTemplateId'].enable();
          this.bipForm.controls['customScheduleId'].enable();
        }

        if (val && Number(val) !== this.selectedOrg && !this.isInitialOrgLoad) {
          this.selectedOrg = Number(val);
          this.bipTemplateList = [];
          this.customScheduleList = [];
          this.getBipTemplates(Number(val));
          this.getCustomSchedules(Number(val));

          this.bipForm.controls['bipTemplateId'].setValue(null);
          this.bipForm.controls['customScheduleId'].setValue(null);
        } else if (
          val &&
          Number(val) !== this.selectedOrg &&
          this.isInitialOrgLoad
        ) {
          this.selectedOrg = Number(val);
          this.getBipTemplates(Number(val));
          this.getCustomSchedules(Number(val));
          this.isInitialOrgLoad = false;
        }
      });

    this.searchInput
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe((val: string) => {
        if (val) {
          if (
            this.selectedStudent &&
            `${this.selectedStudent.firstName} ${this.selectedStudent.lastName}` !==
              val
          ) {
            this.selectedStudent = null;
          }
          this.isStudentSearchLoading = true;
          this.insightsService
            .searchStudentDetail(
              val,
              '',
              this.bipForm.get('organizationId')?.value
            )
            .pipe(
              finalize(() => {
                this.isStudentSearchLoading = false;
              })
            )
            .subscribe({
              next: (res: ApiResponses<StudentSearchModel>) => {
                this.studentList = res.result || [];
                this.cd.markForCheck();
              },
              error: (error: Error) => {
                this.studentList = [];
                this.commonService.notify('error', error.message);
              },
            });
        } else {
          this.studentList = [];
          this.isStudentSearchLoading = false;
        }
      });
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<EditCaseDrawerParams>(DRAWER_IDS.BIP)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          if (this.caseParams) {
            this.caseData = {
              dateOfBirth: this.caseParams?.case?.student.dateOfBirth || '',
              studentId: this.caseParams?.case?.student.enrollId || '',
              firstName: this.caseParams?.case?.student.firstName || '',
              genderId: Number(this.caseParams?.case?.student.genderId),
              lastName: this.caseParams?.case?.student.lastName || '',
              customScheduleId: Number(
                this.caseParams?.case?.student?.customScheduleId
              ),
              organizationId: Number(
                this.caseParams?.case?.student.organizationId
              ),
              raceId: Number(this.caseParams?.case?.student.raceId),
              bipTemplateId: Number(this.caseParams?.case?.bipTemplateId),
              generalEducations:
                this.caseParams?.case?.generalEducations.map(g => {
                  return g.generalEducationId;
                }) || [],
              isEnabledSafetyPlan:
                this.caseParams?.case?.isEnabledSafetyPlan || false,
              isTrainingStudent:
                this.caseParams?.case?.isTrainingStudent || false,
              languageLevelId: Number(this.caseParams?.case?.languageLevelId),
              note: this.caseParams?.case?.note || '',
              specialEducations:
                this.caseParams?.case?.specialEducations.map(g => {
                  return g.specialEducationId;
                }) || [],
            };
            this.selectedOrg = this.caseData.organizationId;
            this.isInitialOrgLoad = true;

            if (this.caseParams.mode === InsightMode.EDIT) {
              this.getBipTemplates(this.caseData.organizationId);
              this.getCustomSchedules(this.caseData.organizationId);
            }
          }
        }
      });
  }

  async getMasterData() {
    this.loaderService.show();
    await Promise.all([
      this.commonService.getDataByType('gender').toPromise(),
      this.commonService.getDataByType('races').toPromise(),
      this.commonService.getDataByType('SpecialEducation').toPromise(),
      this.commonService.getDataByType('generalEducation').toPromise(),
      this.commonService.getDataByType('LanguageLevels').toPromise(),
      this.userData?.isSuperAdmin
        ? this.commonService.getDataByType('Organization', '').toPromise()
        : (this.getAllOrganization(),
          Promise.resolve(undefined as ApiResponses<LookupItem> | undefined)),
    ])
      .then(
        ([
          genderList,
          raceList,
          specialEducationList,
          generalEducationList,
          languageLevelList,
          organizationList,
        ]: [
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
        ]) => {
          this.genderList = genderList ? genderList.result : [];
          this.raceList = raceList ? raceList.result : [];
          this.specialEducationList = specialEducationList
            ? specialEducationList.result
            : [];
          this.generalEducationList = generalEducationList
            ? generalEducationList.result
            : [];
          this.languageLevelList = languageLevelList
            ? languageLevelList.result
            : [];
          this.organizationList = organizationList
            ? organizationList.result
            : [];

          if (
            this.caseParams.mode === InsightMode.EDIT &&
            this.caseData?.organizationId
          ) {
            this.bipForm.controls['organizationId'].setValue(
              this.caseData.organizationId
            );
            this.bipForm.controls['organizationId'].disable();
          } else {
            this.bipForm.controls['organizationId'].setValue(null);
            this.bipForm.controls['organizationId'].enable();
          }

          const selectedOrgId = this.bipForm.controls['organizationId'].value;
          if (selectedOrgId) {
            this.getBipTemplates(Number(selectedOrgId));
            this.getCustomSchedules(Number(selectedOrgId));
          }
          if (this.caseData) {
            const isOrgAvailable = this.organizationList.some(
              org => org.id === this.caseData?.organizationId
            );
            if (!isOrgAvailable) {
              this.getMissingOrganization(this.caseData.organizationId);
            }
          }
        }
      )
      .catch(e => console.error(e))
      .finally(() => this.loaderService.hide());
  }

  getBipTemplates(orgId: number) {
    this.isBipTemplateLoading = true;
    this.insightsService
      .getBipTemplates(orgId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.bipForm.controls['bipTemplateId'].enable();
          this.isBipTemplateLoading = false;
        })
      )
      .subscribe({
        next: (res: ApiResponses<BipTemplateListModel>) => {
          this.bipTemplateList = res.result;
          if (!this.bipForm.controls['bipTemplateId'].value) {
            const defaultTemplate = this.bipTemplateList.find(
              template => template.isDefault
            );
            if (defaultTemplate) {
              this.bipForm.controls['bipTemplateId'].setValue(
                defaultTemplate.id
              );
            }
          }
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  getCustomSchedules(orgId: number) {
    this.isScheduleLoading = true;
    this.scheduleService
      .getAssignedScheduleListByOrgId(orgId, this.IsIncludeDefaultSchedule)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.bipForm.controls['customScheduleId'].enable();
          this.isScheduleLoading = false;
        })
      )
      .subscribe({
        next: (res: ScheduleTemplate[]) => {
          this.customScheduleList = res;
          if (!this.bipForm.controls['customScheduleId'].value) {
            const defaultSchedule = this.customScheduleList.find(
              schedule => schedule.isDefault
            );
            if (defaultSchedule) {
              this.bipForm.controls['customScheduleId'].setValue(
                defaultSchedule.customScheduleId
              );
            }
          }
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  onSearchStudent(event: Event) {
    const searchValue = (event.target as HTMLInputElement).value;
    if (searchValue) {
      this.isStudentSearchLoading = true;
    }
    this.searchInput.next(searchValue);
  }

  getMissingOrganization(orgId: number) {
    this.orgService
      .getOrganization(orgId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (!!res && !!res.id) {
          this.organizationList.push({
            id: res.id,
            value: res.organizationName,
          });
        }
      });
  }

  onSearchOrganization(value: string) {
    this.isOrganizationSearchLoading = true;
    if (this.userData?.isSuperAdmin) {
      this.commonService
        .getDataByType('Organization', value)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.isOrganizationSearchLoading = false;
          })
        )
        .subscribe(orgList => {
          this.organizationList = orgList.result;
        });
    } else {
      this.getAllOrganization();
      this.isOrganizationSearchLoading = false;
      //TODO: Will remove after testing
      // this.manageOrgService
      //   .getAllowedOrganizationsToMoveUser(this.orgId ?? 0, value ?? '', false)
      //   .pipe(
      //     takeUntil(this.destroy$),
      //     finalize(() => {
      //       this.isOrganizationSearchLoading = false;
      //
      //     })
      //   )
      //   .subscribe(orgList => {
      //     this.organizationList = orgList.result;
      //   });
    }
  }

  getAllOrganization() {
    this.commonService.orgList$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgs => {
        this.organizationList = orgs;
      });
  }

  async onBlurStudent(value: string) {
    const selectedStudent = this.selectedStudent
      ? `${this.selectedStudent.firstName} ${this.selectedStudent.lastName}`
      : null;
    const controls = this.bipForm.controls;

    const currentFormValues = this.bipForm.getRawValue();

    if (
      !!selectedStudent &&
      !!this.selectedStudent &&
      selectedStudent === value
    ) {
      const isOrgAvailable = this.organizationList.some(
        org => org.id === this.selectedStudent?.organizationId
      );
      if (!isOrgAvailable) {
        await this.getMissingOrganization(this.selectedStudent.organizationId);
      }

      const preservedValues = {
        specialEducations: currentFormValues.specialEducations,
        generalEducations: currentFormValues.generalEducations,
        languageLevelId: currentFormValues.languageLevelId,
        note: currentFormValues.note,
        isTrainingStudent: currentFormValues.isTrainingStudent,
        isEnabledSafetyPlan: currentFormValues.isEnabledSafetyPlan,
        bipTemplateId: currentFormValues.bipTemplateId,
        customScheduleId: currentFormValues.customScheduleId,
      };

      controls['studentId'].setValue(this.selectedStudent.enrollId);
      controls['studentId'].disable();
      ['firstName', 'lastName', 'genderId', 'dateOfBirth', 'raceId'].forEach(
        ctrl => {
          controls[ctrl].disable();
          controls[ctrl].setValue(
            this.selectedStudent
              ? this.selectedStudent[ctrl as keyof StudentSearchModel]
              : ''
          );
        }
      );

      if (this.selectedStudent.organizationId) {
        const orgControlVal = this.bipForm.controls['organizationId'].value;
        if (
          this.caseParams.mode === InsightMode.EDIT ||
          (!!orgControlVal &&
            Number(orgControlVal) === this.selectedStudent.organizationId)
        ) {
          this.isInitialOrgLoad = true;
          this.selectedOrg = this.selectedStudent.organizationId;
          this.getBipTemplates(this.selectedStudent.organizationId);
          this.getCustomSchedules(this.selectedStudent.organizationId);
        }
      }

      setTimeout(() => {
        Object.keys(preservedValues).forEach(key => {
          if (
            preservedValues[key as keyof typeof preservedValues] !==
              undefined &&
            preservedValues[key as keyof typeof preservedValues] !== null
          ) {
            this.bipForm
              .get(key)
              ?.setValue(preservedValues[key as keyof typeof preservedValues]);
          }
        });
      });
    } else if (value === '') {
      controls['studentId'].enable();
    }
  }

  onStudentSearchBlur() {
    const inputValue = this.bipForm.controls['studentSearch'].value;
    const selectedStudentName = this.selectedStudent
      ? `${this.selectedStudent.firstName} ${this.selectedStudent.lastName}`
      : null;

    if (
      inputValue &&
      selectedStudentName &&
      inputValue !== selectedStudentName
    ) {
      this.bipForm.controls['studentSearch'].setValue('');
      this.onBlurStudent('');
    } else if (!inputValue && this.selectedStudent) {
      this.selectedStudent = null;
    }
  }

  onSelect(student: StudentSearchModel): void {
    this.selectedStudent = student;
    this.bipForm.controls['studentSearch'].setValue(
      `${student.firstName} ${student.lastName}`
    );
    this.onBlurStudent(`${student.firstName} ${student.lastName}`);
    this.studentList = [];
  }

  onSubmit() {
    if (this.bipForm.invalid) {
      return;
    }
    this.loading = true;
    const form = this.bipForm.getRawValue();
    const data = {
      ...form,
      dateOfBirth: format(
        this.bipForm.controls['dateOfBirth'].value,
        'yyyy-MM-dd'
      ),
    };

    const operation$: Observable<
      ApiResponse<number> | ApiResponse<CaseDetailModel>
    > =
      !!this.caseParams.case && this.caseParams.mode === InsightMode.EDIT
        ? this.insightsService.editBipCase(
            data,
            Number(this?.caseParams?.case?.id)
          )
        : this.insightsService.createBipCase(data);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (res: ApiResponse<number> | ApiResponse<CaseDetailModel>) => {
          this.handleSubmissionSuccess(
            !!this.caseParams.case && this.caseParams.mode === InsightMode.EDIT
              ? (res.result as CaseDetailModel)?.id
              : (res.result as number)
          );
        },
        error: (error: Error) => {
          this.handleError(error);
        },
      });
  }

  private handleSubmissionSuccess(caseId: number): void {
    this.showNotification();
    this.closeDrawer();
    const form = this.bipForm.getRawValue();
    const data = {
      ...form,
      dateOfBirth: format(
        this.bipForm.controls['dateOfBirth'].value,
        'yyyy-MM-dd'
      ),
    };
    const selectedOrg = this.organizationList.find(
      org => org.id === data.organizationId
    );
    const payload: BIP = {
      studentId: data.studentId,
      studentName: `${_.startCase(data.firstName)} ${_.startCase(data.lastName)}`,
      caseId: caseId,
      caseOwnerName: data.caseOwnerName || '',
      behaviorCount: 0,
      draftCount: 0,
      caseMembers: [],
      lastModified: new Date().toISOString(),
      organizationName:
        selectedOrg?.value || selectedOrg?.organizationName || '',
      enrollId: '',
    };
    this.openPreBipViewDrawer(payload);
  }

  private showNotification(): void {
    this.commonService.notify(
      'success',
      this.caseParams.mode === InsightMode.EDIT
        ? 'Bip case has been updated successfully'
        : 'Bip case has been created successfully',
      '',
      '450px'
    );
  }

  openPreBipViewDrawer(data: BIP): void {
    this.drawerService.openDrawer(
      ViewBipCaseComponent,
      `${data.studentName}’s Plan`,
      1170,
      true,
      undefined,
      { case: data, mode: InsightMode.VIEW },
      DRAWER_IDS.BIP_DETAIL
    );
  }

  private handleError(error: Error): void {
    this.loading = false;

    this.commonService.notify('error', error.message);
  }

  closeDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.BIP_DETAIL);
    this.drawerService.closeDrawer(DRAWER_IDS.BIP);
  }

  onTrainingStudentChange(value: boolean): void {
    if (value === true) {
      this.previousTrainingValue = this.bipForm.get('isTrainingStudent')?.value;
      this.isTrainingWarningVisible = true;
    }
  }

  handleCancelTrainingWarning(): void {
    this.isTrainingWarningVisible = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
